<script setup>
import InsightChart from '@/containers/components/chart/InsightChart';
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import moment from 'moment/moment';
import { getDefaultMetricPayload } from '@/containers/components/chart/useChart.js';
import { getParamChart } from '@/containers/components/chart/utils.js';
import { useI18n } from 'vue-i18n';
import { useMetricFunction } from './useMetricData';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';
import { EVENT_POPUP } from '@/components/constants/constants';

//Define props
const props = defineProps({
  metric: { type: Object, default: () => ({}) },
  selectedTargets: { type: Array, default: () => [] },
  searchModel: { type: Object, default: () => ({}) },
  dateOption: { type: Object, default: () => ({}) },
  collInterval: { type: String, default: () => '1m' },
  fetchStatus: { type: Boolean, default: false },
  isNow: { type: Boolean, default: false }
});
// Define emits
const emit = defineEmits(['on-zoom', 'add-widget', 'add-event-policy', 'onFetched']);
const { t } = useI18n();
const { $util } = getCurrentInstance().appContext.config.globalProperties;

// Define Ref
const dataInsightChart = ref({
  dataProps: {
    data: [],
    eventName: '',
    eventKey: ''
  }
});
const showTooltipOfChartList = ref({});
const firstFetch = ref(true);
const chartKey = ref();
// Define computed
const inquiryTime = computed(() => [props.dateOption.queryStartDt, props.dateOption.queryEndDt]);
const showTooltipOfChart = computed(() =>
  Object.values(showTooltipOfChartList.value).every((showTooltip) => !!showTooltip)
);

const getSummaryOptions = computed(() => {
  const {
    displayUnit,
    metricName,
    metricUnit,
    fixedUnit,
    productTargetType,
    productTargetTypeEn,
    metricDescription,
    metricDescriptionEn
  } = props.metric;
  return {
    displayUnit,
    metricName,
    metricUnit,
    fixedUnit,
    productTargetType,
    productTargetTypeEn,
    metricDescription,
    metricDescriptionEn,
    productType: props.searchModel.productTypeCode
  };
});

const selectedIndividuals = computed(() => dataInsightChart.value.selectedIndividualItems ?? []);

// Use Composable
const {
  listIndividuals,
  metricData,
  isK8s,
  isGetObject,
  hasSubType,
  getObjectList,
  getK8sObjectList,
  getMetricData
} = useMetricFunction(props, inquiryTime);

// Define Methods
const setDataInsightChart = ({
  interval,
  selectedIndividualItems,
  statisticsTypeList,
  dataProps = { eventName: '', eventKey: '', data: [] }
}) => {
  dataInsightChart.value = {
    ...dataInsightChart.value,
    selectedIndividualItems,
    statisticsTypeList,
    interval,
    dataProps
  };
};

const getParamDownloadChart = () => {
  const [startTime, endTime] = inquiryTime.value;
  return {
    countSelectedObject: selectedIndividuals.value.length,
    countSelectedTargets: props.selectedTargets.length,
    productTypeCode: props.searchModel.productTypeCode,
    params: getParamChart({
      metric: props.metric,
      isDownload: true,
      k8s: isK8s.value,
      hasSubType: hasSubType.value,
      objectType: listIndividuals.value[0]?.objectType || 'pod',
      subTypeCode: props.searchModel.subTypeCode,
      selectedTargets: props.selectedTargets,
      listIndividuals: selectedIndividuals.value,
      startTime,
      endTime
    })
  };
};

const getDataInsightChart = async ({
  selectedIndividualItems = [],
  interval,
  statisticsTypeList,
  eventName = '',
  eventKey = '',
  startTime,
  endTime
}) => {
  await getMetricData({
    selectedIndividualItems,
    statisticsTypeList,
    startTime,
    endTime,
    interval
  });

  setDataInsightChart({
    selectedIndividualItems,
    statisticsTypeList,
    interval,
    dataProps: {
      data: metricData.value,
      eventName,
      eventKey
    }
  });
};

const onChangeOfInsightChart = async (event) => {
  const {
    eventKey,
    eventName,
    query: { startTime, endTime, individualItems, interval, statisticalType }
  } = event;

  await getDataInsightChart({
    selectedIndividualItems: [...individualItems],
    statisticsTypeList: statisticalType,
    interval,
    eventName,
    eventKey,
    startTime: moment(startTime),
    endTime: moment(endTime)
  });
};

const selectIndividualItems = (firstFetch) => {
  const beforeSelectedIndiv = selectedIndividuals.value.filter((value) =>
    listIndividuals.value.some((item) => item.value === value)
  );
  if (firstFetch) {
    listIndividuals.value = listIndividuals.value.map((item) => ({
      ...item,
      isSelected: true
    }));
    return listIndividuals.value.map(({ value }) => value);
  }

  listIndividuals.value = listIndividuals.value.map((item) => ({
    ...item,
    isSelected: beforeSelectedIndiv.includes(item.value)
  }));
  return beforeSelectedIndiv;
};

const getInitDataInsightChart = (firstFetch) => {
  const [startTime, endTime] = inquiryTime.value;
  const { statisticsTypeList, interval } = getDefaultMetricPayload(
    props.searchModel.productTypeCode,
    startTime,
    endTime,
    props.collInterval
  );
  let selectedIndividualItems = selectIndividualItems(firstFetch);
  return getDataInsightChart({
    selectedIndividualItems,
    statisticsTypeList,
    interval,
    startTime,
    endTime
  });
};

const setShowTooltipOfChart = (key, value) => {
  showTooltipOfChartList.value[key] = value;
};

const setChartKey = () => {
  chartKey.value = $util.generateRandomKey();
};
const onInit = async (firstFetch) => {
  if (isGetObject.value) {
    await getObjectList();
  }

  if (isK8s.value) {
    await getK8sObjectList();
  }

  await getInitDataInsightChart(firstFetch);
  setChartKey();
};

// Watchers
watch(
  () => props.fetchStatus,
  async (value) => {
    if (value) {
      await onInit(firstFetch.value);
      if (firstFetch.value) firstFetch.value = false;
      emit('onFetched', 'detail');
    }
  },
  { immediate: true }
);

//Hooks
onMounted(() => {
  for (const EVENT in EVENT_POPUP)
    legoEventBus.$on(EVENT_POPUP[EVENT], (popupShow) => setShowTooltipOfChart(EVENT, !popupShow));
});
onBeforeUnmount(() => {
  for (const EVENT in EVENT_POPUP) legoEventBus.$off(EVENT_POPUP[EVENT]);
});
</script>
<template>
  <div class="metric-detail__container">
    <InsightChart
      :key="chartKey"
      :indivItemOptions="listIndividuals"
      :collInterval="collInterval"
      :inquiryTime="inquiryTime"
      :data="dataInsightChart.dataProps"
      :options="getSummaryOptions"
      :paramDownloadChart="getParamDownloadChart()"
      :showTooltipOfChart="showTooltipOfChart"
      :isNow="isNow"
      @on-change="(event) => onChangeOfInsightChart(event)"
      @on-zoom="$emit('on-zoom')"
      @add-widget="(chartType) => $emit('add-widget', chartType)"
      @add-event-policy="$emit('add-event-policy')"
    />
  </div>
</template>
<style></style>
